import { type ReactNode, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import {
  AddCircleOutline,
  CheckCircle,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useGetAdminChatBotProfileQuery } from "features/adminUsers/adminUsers";

interface Props {
  /** Shown above the fields (the step heading of the internal user form). */
  heading?: ReactNode;
  userId: number | null;
  isCreateMode?: boolean;
  canEdit?: boolean;
  canSeeApiKey?: boolean;
  chatbotDraft?: { chat_id: string; api_key: string } | null;
  onChatbotDraftChange?: (
    data: { chat_id: string; api_key: string },
    serverProfile: { chat_id?: string; api_key?: string } | null | undefined,
  ) => void;
  onSuccess?: (message: string, severity: "success" | "error") => void;
  onSaveComplete?: () => void;
  onSaveAll?: () => void;
  onCreateUser?: () => void;
  createUserDisabled?: boolean;
  hasAnyChanges?: boolean;
  isSaving?: boolean;
}

export default function UserManagementChatBotTab({
  heading,
  userId,
  isCreateMode = false,
  canEdit = true,
  canSeeApiKey = false,
  chatbotDraft,
  onChatbotDraftChange,
  onSuccess,
  onSaveComplete,
  onSaveAll,
  onCreateUser,
  createUserDisabled = false,
  hasAnyChanges = false,
  isSaving = false,
}: Props) {
  const {
    data: profile,
    isLoading,
    isError,
    error,
  } = useGetAdminChatBotProfileQuery(userId!, {
    skip: !userId || isCreateMode,
  });

  const [chatId, setChatId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [syncedFromProfile, setSyncedFromProfile] = useState(false);

  const noProfile =
    isCreateMode || (isError && (error as { status?: number })?.status === 404);
  const hasProfile = !isCreateMode && !isError && !!profile;

  useEffect(() => {
    setSyncedFromProfile(false);
  }, [userId]);

  useEffect(() => {
    if (chatbotDraft !== undefined && chatbotDraft !== null) {
      setChatId(chatbotDraft.chat_id ?? "");
      setApiKey(chatbotDraft.api_key ?? "");
      setSyncedFromProfile(true);
    } else if (hasProfile && profile) {
      setChatId(profile.chat_id ?? "");
      setApiKey(profile.api_key ?? "");
      setSyncedFromProfile(true);
    } else {
      setSyncedFromProfile(false);
    }
  }, [userId, hasProfile, profile?.chat_id, profile?.api_key, chatbotDraft]);

  const handleChatIdChange = (value: string) => {
    setChatId(value);
    onChatbotDraftChange?.(
      { chat_id: value, api_key: apiKey },
      profile ? { chat_id: profile.chat_id, api_key: profile.api_key } : null,
    );
  };
  const handleApiKeyChange = (value: string) => {
    setApiKey(value);
    onChatbotDraftChange?.(
      { chat_id: chatId, api_key: value },
      profile ? { chat_id: profile.chat_id, api_key: profile.api_key } : null,
    );
  };

  if (isLoading && !isCreateMode) {
    return (
      <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={40} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={40} />
      </Box>
    );
  }

  const hasChanges =
    syncedFromProfile &&
    (chatId !== (profile?.chat_id ?? "") ||
      (canSeeApiKey && apiKey !== (profile?.api_key ?? "")));

  return (
    <Box
      sx={{
        px: 2,
        pt: 2,
        pb: 2,
        display: "flex",
        flexDirection: "column",
        flex: 1,
      }}
    >
      {heading}
      {(noProfile || hasProfile) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 2,
            color: hasProfile ? "success.main" : "text.secondary",
            fontSize: "0.875rem",
          }}
        >
          {hasProfile ? (
            <>
              <CheckCircle sx={{ fontSize: 20 }} />
              <Typography variant="body2" color="success.main" fontWeight={600}>
                Profile saved — you can edit the config below
              </Typography>
            </>
          ) : (
            <>
              <AddCircleOutline sx={{ fontSize: 20 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={600}
              >
                First-time setup — no AI Agent profile yet
              </Typography>
            </>
          )}
        </Box>
      )}

      {noProfile && (
        <>
          <TextField
            inputProps={{ autoComplete: "off" }}
            label="Chat ID"
            value={chatId}
            onChange={(e) => canEdit && handleChatIdChange(e.target.value)}
            placeholder="pending"
            size="small"
            fullWidth
            disabled={!canEdit}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
          />
          {canSeeApiKey && (
            <TextField
              label="API Key"
              type={showApiKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => canEdit && handleApiKeyChange(e.target.value)}
              size="small"
              fullWidth
              disabled={!canEdit}
              inputProps={{ autoComplete: "new-password" }}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowApiKey((v) => !v)}
                      edge="end"
                    >
                      {showApiKey ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </>
      )}

      {hasProfile && profile && (
        <>
          <TextField
            label="Chat ID"
            value={chatId}
            onChange={(e) => canEdit && handleChatIdChange(e.target.value)}
            size="small"
            fullWidth
            disabled={!canEdit}
            inputProps={{ autoComplete: "off" }}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
          />
          {canSeeApiKey && (
            <TextField
              label="API Key"
              type={showApiKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => canEdit && handleApiKeyChange(e.target.value)}
              placeholder="Enter API key"
              size="small"
              fullWidth
              disabled={!canEdit}
              inputProps={{ autoComplete: "new-password" }}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowApiKey((v) => !v)}
                      edge="end"
                    >
                      {showApiKey ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          {!canSeeApiKey && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              API Key is visible to Super Admins only.
            </Typography>
          )}
        </>
      )}

      {!noProfile && !hasProfile && !isLoading && (
        <Typography variant="body2" color="text.secondary">
          Unable to load AI Agent profile. Please try again.
        </Typography>
      )}
    </Box>
  );
}
